import { useMemo } from "react";
import {
	useImportEmailAttachmentsConstructionSite,
	useImportEmailAttachmentsGFA,
} from "../../../api/mutations/mail.mutations";
import { useEntityPanelSync } from "../../../hooks/useEntityPanelSync";
import { ConstructionSiteEmailComposerPanel } from "../components/panels/ConstructionSiteEmailComposerPanel";
import ConstructionSiteTaskDetailsPanel from "../components/panels/ConstructionSiteTaskDetailsPanel";

import type { BrokerCommunication, Task } from "@dune/types";
import { generateConstructionSiteEmailWithFallback } from "../../../utils/emails.utils";

interface UseConstructionSitePageStateProps {
	entityType: "CONSTRUCTION_SITE" | "GFA";
	entityId: number;
	communications: BrokerCommunication[];
	tasks: Task[];
	emailRoutingUuid?: string | null;
	emailPrefix?: string;
}

export const useConstructionSitePageState = ({
	entityType,
	entityId,
	communications,
	tasks,
	emailRoutingUuid,
	emailPrefix = "chantier",
}: UseConstructionSitePageStateProps) => {
	const constructionSiteImportMutation =
		useImportEmailAttachmentsConstructionSite(entityId);
	const gfaImportMutation = useImportEmailAttachmentsGFA(entityId);

	const importEmailAttachments =
		entityType === "GFA"
			? gfaImportMutation.mutate
			: constructionSiteImportMutation.mutate;

	const panelComponents = useMemo(
		() => ({
			TaskDetails: ConstructionSiteTaskDetailsPanel,
			EmailComposer: ConstructionSiteEmailComposerPanel,
		}),
		[],
	);

	const panelEmailConfig = useMemo(
		() => ({
			getReplyToEmail: (id: number, routingUuid?: string | null) => {
				if (routingUuid) {
					return generateConstructionSiteEmailWithFallback(
						routingUuid,
						id,
						false,
					);
				}
				return `${emailPrefix}-${id}@dune.com`;
			},
			importEmailAttachmentsHandler: (emailAttachmentId: number) => {
				importEmailAttachments({ emailAttachmentId });
			},
		}),
		[importEmailAttachments, emailPrefix],
	);

	useEntityPanelSync({
		entityType,
		entityId,
		communications,
		tasks,
		emailRoutingUuid,
		components: panelComponents,
		emailConfig: panelEmailConfig,
	});
};
